import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import type { ConsumptionEntry } from '../../../services/dataService';
import { colorForIndex } from '../../../utils/colors';
import * as colors from '../../../../../common/styles/colors';
import { barThickness } from '../../../../../common/styles/global/chart.styles';
import { getCustomTooltipHandler } from './utils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

type MultiSeries = Array<{ label: string; entries: ConsumptionEntry[] }>;

interface DailyChartProps {
  mode?: 'single' | 'multi';
  data?: ConsumptionEntry[]; // single
  series?: MultiSeries; // multi
  onDayClick?: (date: string) => void; // YYYY-MM-DD
  yearMonth?: string; // For title
  selectedMpans: string[];
}

function formatDate(isoStart: string) {
  return isoStart.slice(0, 10); // YYYY-MM-DD
}

const DailyChart: React.FC<DailyChartProps> = ({
  mode = 'single',
  data = [],
  series = [],
  onDayClick,
  yearMonth,
  selectedMpans,
}) => {
  const hasDailyData = () => {
    if (mode === 'single') {
      if (Object.keys(data).length > 0 && Object.values(data).some(item => item.totalConsumption > 0)) {
        return true;
      }
      return false;
    } else {
      if (
        Object.keys(series).length > 0 &&
        Object.values(series).some(arr => arr.entries && arr.entries.some(item => item.totalConsumption > 0))
      ) {
        return true;
      }
      return false;
    }
  };

  const chartDataSingle = useMemo(() => {
    const labels = data.map(d => new Date(d.intervalStart).getDate().toString());

    // set value to `null` so it won't take space on graph
    const actual = data.map((d: ConsumptionEntry): number | null => {
      if (d.actualConsumption > 0) return d.actualConsumption;
      return null;
    });

    // set value to `null` so it won't take space on graph
    const estimated = data.map((d: ConsumptionEntry): number | null => {
      if (d.estimatedConsumption > 0) return d.estimatedConsumption;
      return null;
    });

    return {
      labels,
      datasets: [
        {
          data: actual,
          skipNull: true,
          backgroundColor: colors.hydroBlue,
          borderColor: colors.hydroBlue,
          borderWidth: 1,
          maxBarThickness: barThickness,
          stack: 'stack0',
        },
        {
          data: estimated,
          skipNull: true,
          backgroundColor: colors.draxDarkBlue,
          borderColor: colors.draxDarkBlue,
          borderWidth: 1,
          maxBarThickness: barThickness,
          stack: 'stack0',
        },
      ],
    };
  }, [data]);

  const chartDataMulti = useMemo(() => {
    const labels = (series.find(s => s.entries?.length)?.entries || []).map(d =>
      new Date(d.intervalStart).getDate().toString()
    );

    const datasets = (series || []).map((s, i) => {
      const { stroke, fill } = colorForIndex(i);
      return {
        label: s.label,
        data: (s.entries || []).map(d => d.totalConsumption),
        borderColor: stroke,
        backgroundColor: fill,
        tension: 0.2,
        pointRadius: 3,
      };
    });
    return { labels, datasets };
  }, [series]);

  const options = useMemo(() => {
    let title = 'Daily Consumption';
    if (yearMonth) {
      const [year, month] = yearMonth.split('-');
      const date = new Date(Number(year), Number(month) - 1);
      const monthName = date.toLocaleString(undefined, { month: 'long' });
      title = `Daily Consumption - ${monthName} ${year}`;

      if (series && series.length > 0) title = `MPAN Comparison - Daily Consumption - ${monthName} ${year}`;

      if (selectedMpans && selectedMpans.length === 1)
        title = `${selectedMpans[0]} - Daily Consumption - ${monthName} ${year}`;
    }
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: {
          display: true,
          text: title,
          font: {
            size: 16, // Set the font size in pixels
            weight: 400,
            family: `Rubrik, 'Calibri'`,
          },
          color: colors.hydroBlue,
        },
        tooltip: {
          // https://www.chartjs.org/docs/latest/configuration/tooltip.html
          enabled: false,
          mode: 'point',
          external: getCustomTooltipHandler('kWh'),
        },
      },
      onClick: (_evt: any, elements: any[]) => {
        if (!onDayClick || !elements?.length) return;
        const idx = elements[0].index;
        const seriesEntry = series.find(s => s.entries?.length)?.entries || [];
        const entry = (mode === 'single' ? data : seriesEntry)[idx];
        if (entry) onDayClick(formatDate(entry.intervalStart));
      },
      scales: {
        x: {
          title: { display: true, text: 'Day', color: colors.hydroBlue },
          ticks: { minRotation: 90, maxRotation: 90, color: colors.hydroBlue },
          grid: { display: false },
        },
        y: {
          title: { display: true, text: 'kWh', color: colors.hydroBlue },
          ticks: {
            color: colors.hydroBlue,
          },
          beginAtZero: true,
        },
      },
    };
  }, [data, series, onDayClick, yearMonth, mode]);

  return (
    <div className='pl-3' style={{ height: 500 }}>
      {hasDailyData() ? (
        mode === 'single' ? (
          <Bar data={chartDataSingle} options={options as any} />
        ) : (
          <Line data={chartDataMulti} options={options as any} />
        )
      ) : (
        <div className='d-flex justify-content-center'>No data available for this period</div>
      )}
    </div>
  );
};

export default DailyChart;
